import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  width: max-content;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const HeadRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const HeadLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const PeriodTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const SubMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-wrap: wrap;
`;

export const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

/** Кнопка действия + видимая подсказка-причина под ней (не только title-тултип:
 *  тултип недоступен с клавиатуры и на тач-экранах). */
export const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const ActionHint = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.muted};
  text-align: right;
  max-width: 220px;
`;

/** Решение по обработке — право-действие `econ.bdr.approve`, а не право записи
 *  (ТЗ п. 6.10, Д8). Бэкенд проверяет то же самое и отвечает 403. */
export const NO_DECISION_RIGHT =
  'Решение принимает сотрудник с правом утверждать БДР: главный экономист или руководитель';
export const OWN_RUN_HINT = 'Свою обработку утверждает другой сотрудник с правом утверждения';

/** Полоса сверки периода обработки с периодом из документов. `$mismatch` красит
 *  рамку/фон в danger при расхождении, иначе нейтральный info-тон. */
export const PeriodCheck = styled.div<{ $mismatch: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  border: 1px solid
    ${({ theme, $mismatch }) =>
      $mismatch ? `${theme.colors.status.danger}55` : theme.colors.border};
  background: ${({ theme, $mismatch }) =>
    $mismatch ? `${theme.colors.status.danger}10` : theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PeriodLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const PeriodValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const MismatchNote = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-weight: 600;
`;

/** Полоса статуса полноты обработки под шапкой. `$tone` красит рамку/фон в
 *  danger (blocking) либо warning (gaps) — как `PeriodCheck`/`UnmappedBox`.
 *  Для `complete` блок не рендерится вовсе (обычный бейдж статуса в шапке). */
export const CompletenessBox = styled.div<{ $tone: 'danger' | 'warning' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $tone }) => `${theme.colors.status[$tone]}55`};
  background: ${({ theme, $tone }) => `${theme.colors.status[$tone]}10`};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.5;
`;

export const CompletenessHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const CompletenessTitle = styled.strong<{ $tone: 'danger' | 'warning' }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme, $tone }) => theme.colors.status[$tone]};
`;

export const CompletenessExplain = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SectionTitle = styled.h3`
  margin: ${({ theme }) => theme.spacing(2)} 0 ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  font-weight: 650;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const SectionLead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Горизонтальный скролл для широких таблиц на узких экранах (≤360px) —
 *  страница не «уезжает», скроллится только таблица. */
export const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.surface};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Имя файла-источника в колонке «Где в документе»: первой строкой, заметнее
 *  ссылки на лист/страницу (при нескольких файлах в обработке без него строки
 *  разных документов неотличимы). Длинные имена обрезаются с многоточием. */
export const SourceFile = styled.div`
  max-width: 220px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ── Блок «Результат»: показатели, сгруппированные по объекту ───────────────── */

export const ObjectGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing(5)};
  }
`;

export const ObjectHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const ObjectName = styled.div`
  font-size: 14px;
  font-weight: 650;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const MetricList = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  margin: 0;
  align-items: baseline;
`;

export const MetricLabel = styled.dt`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 0;
`;

export const MetricNote = styled.span`
  display: block;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Пометка «заведомо неполный» рядом с итоговым денежным показателем, когда
 *  обработка неполна (не все источники вошли). Warning-тон, мелкий капс —
 *  как подпись-предупреждение под значением, не перекрывает само число
 *  (экономисту полезно видеть, что есть, но с явной оговоркой). */
export const IncompleteTag = styled.span`
  display: block;
  max-width: 200px;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.status.warning};
  white-space: normal;
  text-align: right;
`;

export const MetricValue = styled.dd`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const MetricDelta = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Подпись разреза факта (статья / объект / лицензия / способ оплаты) под
 *  названием метрики. Вторичный цвет и меньший кегль — визуально отделяет
 *  разрез от названия показателя, чтобы десятки фактов одной метрики читались
 *  по различию, а не сливались. */
export const MetricDim = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};

  b {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Кнопка свёртки «ещё N» / «свернуть» для длинных списков фактов одной метрики
 *  внутри договора. Занимает обе колонки `MetricList` (через grid-column). */
export const MoreToggle = styled.button`
  grid-column: 1 / -1;
  justify-self: start;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-left: ${({ theme }) => `-${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/* ── Свёрнутый блок «Источники (трассировка)» ──────────────────────────────── */

export const TraceToggle = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 15px;
  font-weight: 650;
  cursor: pointer;
  padding: 0;
  text-align: left;

  svg {
    color: ${({ theme }) => theme.colors.text.muted};
    flex-shrink: 0;
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }
`;

export const TraceBody = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

export const FactRowButton = styled.button<{ $open: boolean }>`
  border: none;
  background: ${({ theme, $open }) => ($open ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

export const PayloadList = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  margin: 0;
`;

export const PayloadKey = styled.dt`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

export const PayloadVal = styled.dd`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-word;
`;

export const RowEditButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const EditCell = styled.td`
  background: ${({ theme }) => theme.colors.background};
`;

export const PayloadEditor = styled.textarea<{ $invalid: boolean }>`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  border: 1px solid
    ${({ theme, $invalid }) => ($invalid ? theme.colors.status.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.colors.status.danger : theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const EditHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

export const EditError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

export const ErrBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}12;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

/** Заметный баннер провала фоновой сборки отчёта XLSX (после «Утвердить»).
 *  Danger-тон, как `ErrBox`/`PeriodCheck`, но с явным заголовком, текстом
 *  причины (как есть, по-русски с backend) и подсказкой к повторному
 *  утверждению. Причина приходит в `run.summary.report_error` — раньше была
 *  видна только в серверных логах. */
export const ReportErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}10;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}55;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.5;
`;

export const ReportErrorTitle = styled.strong`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export const ReportErrorReason = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ReportErrorHint = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

/** Предупреждающий (не критический) баннер: по части договоров выручка
 *  посчитана, но они не привязаны к карте активного шаблона — в собранном XLSX
 *  по ним выйдет 0. Warning-тон (`status.warning`), а не danger: расчёт верен,
 *  отчёт соберётся, но цифры по этим договорам в файл не попадут. Раскладка —
 *  как у `ReportErrorBox`, но в жёлтой гамме. */
export const UnmappedBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.warning}14;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.5;
`;

export const UnmappedTitle = styled.strong`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const UnmappedLead = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Перечень непривязанных договоров: № + форма + сумма. Без маркеров списка,
 *  суммы выровнены вправо моноширинными цифрами, чтобы читались столбиком. */
export const UnmappedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const UnmappedItem = styled.li`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  border-top: 1px solid ${({ theme }) => theme.colors.status.warning}22;
`;

export const UnmappedContract = styled.span`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

export const UnmappedMetric = styled.span`
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const UnmappedRevenue = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const UnmappedHint = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

/* ── Блок живого прогресса обработки (обновляется по WS-событию) ────────────── */

export const ProgressBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ProgressTopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const ProgressStage = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProgressPercent = styled.span`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
  flex-shrink: 0;
`;

export const ReviewBar = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.gradient.surface};
`;

export const ReviewText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const TraceCell = styled.td`
  background: ${({ theme }) => theme.colors.background};
`;
